import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Award, X, ArrowRight, Loader2, Tag } from "lucide-react";
import { getConsignments } from "../lib/api";
import { MaskLine } from "../components/Reveal";

function StatusBadge({ status }) {
  const sold = status === "sold";
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em] px-2.5 py-1 font-semibold ${sold ? "bg-red-900/70 text-red-200" : "bg-gold text-black"}`}
      data-testid="consignment-status"
    >
      {sold ? "Sold" : "Available"}
    </span>
  );
}

export default function ConsignmentsPage() {
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    getConsignments().then(setItems).catch(() => setItems([]));
  }, []);

  const available = useMemo(() => (items || []).filter((i) => i.status !== "sold").length, [items]);

  const open = (it) => { setImgIdx(0); setActive(it); };

  return (
    <main className="pt-[72px]" data-testid="consignments-page">
      <section className="py-20 sm:py-28 border-t border-[#27272a]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-14">
            <div className="lg:col-span-8">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">[ South East Collectibles · Consignments ]</span>
              <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-semibold uppercase tracking-tighter leading-[0.9] mt-5">
                <MaskLine>Current</MaskLine>
                <MaskLine delay={0.1} className="text-gold">Consignments</MaskLine>
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="text-zinc-400 leading-relaxed">
                Standout cards we're representing right now. Enquire to purchase or to
                consign your own grails with us — we handle cleaning, grading and sale
                end to end.
              </p>
              {items && (
                <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-500 mt-4">
                  {available} available {available === 1 ? "piece" : "pieces"}
                </p>
              )}
            </div>
          </div>

          {!items ? (
            <div className="flex items-center gap-3 text-zinc-500"><Loader2 className="animate-spin text-gold" size={18} /> Loading consignments…</div>
          ) : !items.length ? (
            <div className="border border-[#27272a] bg-[#0e0e0e] p-12 text-center" data-testid="no-consignments">
              <Tag className="text-gold mx-auto mb-4" size={26} />
              <p className="font-display text-2xl">No consignments listed yet</p>
              <p className="text-zinc-500 text-sm mt-2 max-w-md mx-auto">
                We're curating our next drop. Check back soon — or get in touch to consign a card with us.
              </p>
              <Link to="/contact" className="inline-flex items-center gap-2 mt-6 rounded-full bg-gold text-black font-mono text-xs uppercase tracking-[0.12em] px-6 py-3.5 font-semibold hover:bg-[#d59e19] transition-colors" data-testid="no-consignments-enquire">
                Enquire To Consign <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="consignments-grid">
              {items.map((it, i) => (
                <motion.button
                  key={it.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: (i % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => open(it)}
                  className="group text-left relative overflow-hidden border border-[#27272a] hover:border-gold/50 transition-colors duration-500 focus:outline-none focus:border-gold"
                  data-testid={`consignment-${it.id}`}
                >
                  <div className="relative aspect-[3/4] bg-gradient-to-b from-[#161616] to-[#0b0b0b]">
                    {it.images?.[0] ? (
                      <img src={it.images[0]} alt={it.title} loading="lazy" className="w-full h-full object-contain p-3 group-hover:scale-[1.04] transition-transform duration-700 ease-out" />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-zinc-700"><Tag size={30} /></div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent pointer-events-none" />
                    <span className="absolute top-3 right-3"><StatusBadge status={it.status} /></span>
                    {it.grade && (
                      <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-black/70 text-gold border border-gold/40 font-mono text-[10px] uppercase tracking-[0.1em] px-2 py-1 font-semibold">
                        <Award size={11} /> {it.grade}
                      </span>
                    )}
                    {it.images?.length > 1 && (
                      <span className="absolute bottom-3 right-3 font-mono text-[10px] text-zinc-300 bg-black/60 px-2 py-0.5">+{it.images.length - 1}</span>
                    )}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-end justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-display text-base font-semibold leading-tight truncate">{it.title}</h3>
                        {it.player && <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-zinc-400 mt-1 truncate">{it.player}</p>}
                      </div>
                      {it.price && <span className="font-mono text-gold text-sm whitespace-nowrap">{it.price}</span>}
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] grid place-items-center p-5 bg-black/85 backdrop-blur-sm overflow-y-auto"
            onClick={() => setActive(null)}
            data-testid="consignment-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-4xl bg-[#111] border border-white/10 my-8"
            >
              <div className="flex justify-end p-3">
                <button onClick={() => setActive(null)} className="text-zinc-500 hover:text-white" data-testid="consignment-modal-close" aria-label="Close"><X size={24} /></button>
              </div>
              <div className="grid md:grid-cols-2 gap-8 p-6 pt-0 sm:p-8 sm:pt-0">
                <div>
                  <div className="relative aspect-[3/4] bg-gradient-to-b from-[#181818] to-[#0b0b0b] border border-[#27272a]">
                    {active.images?.[imgIdx] ? (
                      <img src={active.images[imgIdx]} alt={active.title} className="w-full h-full object-contain p-4" />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-zinc-700"><Tag size={40} /></div>
                    )}
                  </div>
                  {active.images?.length > 1 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {active.images.map((url, idx) => (
                        <button key={url} onClick={() => setImgIdx(idx)} className={`h-16 w-16 border overflow-hidden transition-colors ${idx === imgIdx ? "border-gold" : "border-[#27272a] hover:border-zinc-500"}`} data-testid={`consignment-thumb-${idx}`}>
                          <img src={url} alt="" className="h-full w-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <StatusBadge status={active.status} />
                    {active.grade && <span className="inline-flex items-center gap-1.5 text-gold font-mono text-xs"><Award size={13} /> {active.grade}</span>}
                  </div>
                  <h2 className="font-display text-3xl font-semibold leading-tight">{active.title}</h2>
                  {active.player && <p className="font-mono text-xs uppercase tracking-[0.14em] text-zinc-400 mt-2">{active.player}</p>}
                  {active.price && <p className="font-display text-2xl text-gold mt-4">{active.price}</p>}
                  {active.description && <p className="text-zinc-300 text-sm leading-relaxed mt-5 whitespace-pre-line">{active.description}</p>}
                  {active.status !== "sold" && (
                    <Link
                      to={`/contact?subject=${encodeURIComponent(`Consignment enquiry — ${active.title}`)}`}
                      className="inline-flex items-center gap-2 mt-7 rounded-full bg-gold text-black font-mono text-xs uppercase tracking-[0.14em] px-6 py-4 font-semibold hover:bg-[#d59e19] transition-colors"
                      data-testid="consignment-enquire"
                    >
                      Enquire About This Card <ArrowRight size={15} />
                    </Link>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
